import React, { useEffect, useRef, useState } from 'react';
import Code from '@/components/code';
import RadioTabs from './RadioTabs';
import { CopyButton } from './CopyButton';

interface ApiCardProps {
    title?: string;
    modelString?: string;
    codeSnippets?: {
        [key: string]: string;
    };
    className?: string;
    children?: React.ReactNode;
    headerTabs?: React.ReactNode;
}

const ApiCard = ({
    title,
    modelString,
    codeSnippets,
    className = '',
    children,
    headerTabs
}: ApiCardProps) => {
    const [selectedLanguage, setSelectedLanguage] = useState('cURL');
    const [isTextTruncated, setIsTextTruncated] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const checkTruncation = () => {
            if (textRef.current && modelString) {
                const textElement = textRef.current;
                // Check if text is overflowing by comparing scrollWidth vs clientWidth
                const isTruncated = textElement.scrollWidth > textElement.clientWidth;
                setIsTextTruncated(isTruncated);
            }
        };

        // Check after render
        const timer = setTimeout(checkTruncation, 0);

        // Also check on resize
        window.addEventListener('resize', checkTruncation);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', checkTruncation);
        };
    }, [modelString]);

    return (
        <div
            className={`flex flex-col w-full ${className}`}
            style={{
                maxWidth: '49.25rem',
                flexShrink: 0,
                borderRadius: '1rem',
                border: '1px solid #E5E5E5',
                background: '#FFF'
            }}
        >
            {(title || modelString || headerTabs) && (
                <div className="border-b border-[#E5E5E5]">
                    {(title || modelString) && (
                        <div className="flex flex-col sm:flex-row gap-4 p-4 sm:px-6 relative">
                            {title && (
                                <div className="flex items-center gap-4 sm:w-auto flex-shrink-0 mr-[4rem]">
                                    <div className="text-sm uppercase whitespace-nowrap">{title}</div>
                                    {headerTabs && (
                                        <div className="ml-auto">
                                            {headerTabs}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Vertical divider line - only if both title and modelString exist */}
                            {title && modelString && (
                                <div
                                    className="hidden sm:block w-px bg-[#DBDBDB] flex-shrink-0"
                                    style={{
                                        strokeWidth: '1px',
                                        stroke: '#DBDBDB',
                                        marginTop: '-1rem',
                                        marginBottom: '-1rem',
                                        minHeight: 'calc(100% + 2rem)'
                                    }}
                                ></div>
                            )}
                            {modelString && (
                                <div className="flex gap-3 items-center justify-between flex-1 text-left">
                                    <div className={`text-sm flex-shrink-0 whitespace-nowrap ${isTextTruncated ? 'hidden sm:block' : ''}`}>Model Short String</div>
                                    <div
                                        ref={textRef}
                                        className="text-sm flex-1 overflow-hidden"
                                        style={{
                                            textDecoration: 'underline',
                                            textUnderlineOffset: '3px',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {modelString}
                                    </div>
                                    <CopyButton
                                        textToCopy={modelString}
                                        variant="dark"
                                        size={24}
                                        tooltipText="Copy model string"
                                        successTooltipText="Model string copied!"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <>
                {codeSnippets ? (
                    <>
                        <RadioTabs
                            options={Object.keys(codeSnippets)}
                            selectedOption={selectedLanguage}
                            onChange={setSelectedLanguage}
                            className="mb-4"
                        />
                        <Code
                            codeString={codeSnippets[selectedLanguage]}
                            language={selectedLanguage.toLowerCase()}
                            title={`${selectedLanguage} Example`}
                        />
                    </>
                ) : children}
            </>
        </div>
    );
};

export default ApiCard;